import React, { useEffect, useState } from "react";
import {
  UncontrolledDropdown,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
} from "reactstrap";
import { Modal, Table } from "antd";
import { ReactTyped } from "react-typed";
import { FormProvider, useForm } from "react-hook-form";
import budgetApis from "../../../api/master/budget";
import { uploadApis } from "../../../api/Uploads";
import { Icon } from "../../../components/Component";
import FormInput from "../../form-components/InputComponent";
import ExportToXlsx from "../../../utils/exportToXlsx";

const blobUrl: String = process.env.REACT_APP_BLOB_URL || "";
const blobContainer: String = process.env.REACT_APP_BLOB_CONTAINER || "";

const BudgetLineBulkUpload = ({
  onUpload,
  budgetCategoryOptions,
  departmentOptions,
  budgetStartDate,
  budgetEndDate,
  existingLines = [],
  siteId,
}: {
  onUpload: (budgetLines: any[]) => void;
  budgetCategoryOptions: any[];
  departmentOptions?: any[];
  budgetStartDate: string;
  budgetEndDate: string;
  existingLines?: any[];
  siteId?: string;
}) => {
  const [loading, setLoading] = useState(false);
  const [blockEdit, setBlockEdit] = useState(false);
  const [response, setResponse] = useState({
    status: "",
    message: "",
  });
  const [showModal, setShowModal] = useState(false);

  const fetchAndExportContractors = async () => {
    try {
      if (!siteId) return;
      const response = await uploadApis.fetchProcurementReferences(siteId, [
        "PREFERRED_CONTRACTORS",
      ]);
      const refs = Array.isArray(response) ? response : response ? [response] : [];
      const allContractors: any[] = [];
      refs.forEach((site: any) => {
        if (site?.contractorsWithSitePreference && Array.isArray(site.contractorsWithSitePreference)) {
          site.contractorsWithSitePreference.forEach((contractor: any) => {
            allContractors.push({
              ID: contractor.id,
              "Contractor Name": contractor.companyName,
            });
          });
        }
      });
      if (allContractors.length === 0) {
        return;
      }
      ExportToXlsx(allContractors, "Contractors");
    } catch (error) {
      console.log("Error fetching contractors:", error);
    }
  };

  const methods = useForm({
    mode: "onChange",
  });

  const {
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = methods;

  const files = watch("attachment");

  useEffect(() => {
    setResponse({ status: "", message: "" });
  }, [files]);

  const handleUpload = async () => {
    try {
      const attachment = files;
      if (!attachment || !attachment[0]) {
        setResponse({
          status: "error",
          message: "Please select a file",
        });
        setBlockEdit(false);
        setLoading(false);
        return;
      }
      uploadFile(attachment, setResponse, "File Uploaded Successfully");
    } catch (error) {
      console.log("Upload error:", error);
    }
  };

  const uploadFile = async (
    attachment: any,
    setResponse: any,
    successResponse: any
  ) => {
    try {
      const _file = attachment?.[0];
      const formData: any = new FormData();
      formData.append("files", _file);

      // Create request payload for Budget Line upload
      // Include existing lines with budgetLineName and budgetCategoryName
      const existingLinesPayload = existingLines.map((line: any) => ({
        budgetLineName: line.budgetLineName,
        budgetCategoryName: line.budgetCategoryName || line.customBudgetCategory,
      }));

      const request = {
        startDate: budgetStartDate,
        endDate: budgetEndDate,
        lines: existingLinesPayload,
      };

      formData.append("request", JSON.stringify(request));

      // Call the API for Budget Line bulk upload
      const res: any = await budgetApis.budgetLineBulkUploadValidate(formData);

      const { status, error, message, response: data } = res || {};

      if (status) {
        setResponse({
          status: "success",
          message: successResponse,
        });

        // Process the uploaded Budget Lines
        if (data && data.lines) {
          const processedBudgetLines = data.lines.map(
            (item: any, index: number) => ({
              key: `bulk_${index}_${Date.now()}`,
              budgetCategoryId: item.budgetCategoryId?.toString(),
              customBudgetCategory:
                item.customBudgetCategory || item.budgetCategoryName || null,
                budgetCategoryName: item.budgetCategoryName,
              budgetLineName: item.budgetLineName,
              budgetValue: item.budgetValue,
              startDate: item.startDate,
              endDate: item.endDate,
              bom: item.bom || "N",
              allocation: item.allocation || null,
              lineDescription: item.lineDescription,
              currencyCode: item.currencyCode || "INR",
              bomLines: item.bomLines || [],
              errorFlag: item.errorFlag,
              errorMessage: item.errorMessage,
              lineId: Math.floor(Math.random() * 10000),
              department: item.department,
              departmentName: item.departmentName,
              contractorId: item.contractorId?.toString() || "",
              contractorName: item.contractorName || "",
              budgetFor: item.budgetFor || "",
              status: "Draft", // Default status for new budget lines
              budgetId: "", // Will be set when budget is saved
              budgetLineId: "", // Will be set when budget is saved
            })
          );

          // Call the parent component's onUpload function
          onUpload(processedBudgetLines);

          // Close modal immediately after upload
          setShowModal(false);
          setBlockEdit(false);
          setValue("attachment", null);
          setLoading(false);
          setResponse(() => ({
            status: "",
            message: "",
          }));
        }
        return;
      }

      setResponse({
        status: "error",
        message: error + ": " + message,
      });
      setBlockEdit(false);
      setLoading(false);
    } catch (error) {
      console.log("Upload file error:", error);
      setResponse({
        status: "error",
        message: "An error occurred while uploading the file",
      });
      setBlockEdit(false);
      setLoading(false);
    }
  };

  return (
    <>
      <UncontrolledDropdown size={"md"}>
        <div
          onClick={() => {
            setShowModal(true);
          }}
          className="btn btn-white btn-outline-light"
        >
          <Icon name="upload-cloud"></Icon>
          <span>Upload Budget Lines</span>
        </div>
      </UncontrolledDropdown>

      <Modal
        title="Bulk Budget Line Upload"
        open={showModal}
        footer={null}
        onCancel={() => {
          setShowModal(false);
          setBlockEdit(false);
          setValue("attachment", null);
          setLoading(false);
          setResponse(() => ({
            status: "",
            message: "",
          }));
        }}
        width={"60%"}
      >
        <FormProvider {...methods}>
          <form>
            <p>
              You're about to upload multiple budget lines at once. To ensure a
              smooth import, please{" "}
              <a
                href={`${blobUrl}${blobContainer}/BudgetLinesUpload.xlsx`}
                target="_self"
                style={{
                  textDecoration: "underline",
                }}
              >
                Download Template
              </a>{" "}
              and fill in the required budget line data accurately and
              completely, then upload the file below. You can check{" "}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  const exportData = (budgetCategoryOptions || []).map(
                    (item: any) => ({
                      ID: item.value?.toString(),
                      "Budget Category": item.label,
                    })
                  );
                  ExportToXlsx(exportData, "Budget Categories");
                }}
                target="_self"
                style={{ textDecoration: "underline" }}
              >
                Budget Categories
              </a>{" "}
              and{" "}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  const exportData = (departmentOptions || []).map((dept: any) => ({
                    ID: dept.value || dept.deptID?.toString(),
                    "Department Name": dept.label || dept.deptName,
                  }));
                  ExportToXlsx(exportData, "Departments");
                }}
                target="_self"
                style={{ textDecoration: "underline" }}
              >
                Departments
              </a>
              {", "}
              <a
                href="#"
                onClick={async (e) => {
                  e.preventDefault();
                  await fetchAndExportContractors();
                }}
                target="_self"
                style={{ textDecoration: "underline" }}
              >
                Contractors
              </a>
              {", "}
              <a
                href="#"
                onClick={(e) => {
                  e.preventDefault();
                  ExportToXlsx(
                    [
                      { "Budget For": "Material" },
                      { "Budget For": "Service/Work" },
                    ],
                    "Budget For"
                  );
                }}
                target="_self"
                style={{ textDecoration: "underline" }}
              >
                Budget For
              </a>{" "}
              for reference
            </p>

            <div className="text-right mt-3"></div>
            <p>
              <strong>Note : </strong>
              All fields in the template are mandatory. Each record must have
              unique information to avoid errors during the import process.
            </p>
            <Col md={4}>
              {files && files[0]?.name && (
                <div className="form-group m-0">
                  <div className="form-label-wrapper">
                    <label className="form-label" htmlFor={"attachment"}>
                      File Upload Area
                    </label>
                  </div>
                  <div
                    className="form-control-wrap d-flex align-items-center justify-content-between border p-1"
                    style={{ borderRadius: "3px" }}
                  >
                    <span>{files && files[0]?.name}</span>
                    {!blockEdit && (
                      <span
                        onClick={() => {
                          setValue("attachment", null);
                        }}
                        style={{
                          cursor: "pointer",
                          marginLeft: "10px",
                          display: "block",
                          fontSize: 14,
                        }}
                      >
                        <Icon name="cross"></Icon> &nbsp;
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div
                style={{
                  display: `${files && files[0]?.name ? "none" : "block"}`,
                }}
              >
                <FormInput
                  type="file"
                  name="attachment"
                  label="File Upload Area"
                  placeholder="File Upload Area"
                  accept=".xlsx"
                />
              </div>
            </Col>

            <Alert
              isOpen={response?.status === "error"}
              color="danger"
              className="start-0 w-100 py-1 mt-2"
              style={{
                top: 0,
                textAlign: "left",
                fontSize: "14px",
                cursor: "default",
              }}
            >
              <ReactTyped
                strings={[response?.message]}
                typeSpeed={30}
                showCursor={false}
              />
            </Alert>

            <Alert
              isOpen={response?.status === "success"}
              color="success"
              className="start-0 w-100 py-1 mt-2"
              style={{
                top: 0,
                textAlign: "left",
                fontSize: "14px",
                cursor: "default",
              }}
            >
              <p>
                {response?.message}, You can{" "}
                <span
                  className="inlineTableCellLink"
                  onClick={() => {
                    setShowModal(false);
                    setBlockEdit(false);
                    setValue("attachment", null);
                    setLoading(false);
                    setResponse(() => ({
                      status: "",
                      message: "",
                    }));
                  }}
                >
                  close
                </span>{" "}
                this popup
              </p>
            </Alert>

            <Row className="mt-4">
              <Col>
                {response?.status == "success" ? (
                  <></>
                ) : (
                  <ul className="actions clearfix d-flex">
                    <li>
                      {loading ? (
                        <Button color="primary" type="button" size="md">
                          <Spinner size="sm" color="light" />
                        </Button>
                      ) : (
                        <Button
                          color="primary"
                          type="button"
                          disabled={!files?.[0]}
                          onClick={() => {
                            setResponse(() => ({
                              status: "",
                              message: "",
                            }));
                            setBlockEdit(true);
                            setLoading(true);
                            handleUpload();
                          }}
                        >
                          Upload
                        </Button>
                      )}
                    </li>
                    {!loading && (
                      <li className="m-3 mt-0">
                        <Button
                          color="primary"
                          onClick={() => {
                            setShowModal(false);
                            setValue("attachment", null);
                          }}
                          className="btn-dim btn btn-secondary"
                        >
                          Cancel
                        </Button>
                      </li>
                    )}
                  </ul>
                )}
              </Col>
            </Row>
          </form>
        </FormProvider>
      </Modal>
    </>
  );
};

export default BudgetLineBulkUpload;
